import { Extension } from "@tiptap/core";
import { NodeSelection, Plugin, PluginKey } from "@tiptap/pm/state";
import type { EditorView } from "@tiptap/pm/view";

/** Select the top-level block at `pos` and start a ProseMirror move-drag with it. */
export function startBlockDrag(view: EditorView, pos: number, dt: DataTransfer): void {
  if (!view.editable) return;
  const node = view.state.doc.nodeAt(pos);
  if (!node) return;
  view.dispatch(view.state.tr.setSelection(NodeSelection.create(view.state.doc, pos)));
  const slice = view.state.selection.content();
  const { dom, text } = view.serializeForClipboard(slice);
  dt.clearData();
  dt.setData("text/html", dom.innerHTML);
  dt.setData("text/plain", text);
  dt.effectAllowed = "copyMove";
  const nodeDom = view.nodeDOM(pos);
  if (nodeDom instanceof HTMLElement) dt.setDragImage(nodeDom, 0, 0);
  view.dragging = { slice, move: true };
}

function topLevelPosAt(view: EditorView, event: MouseEvent): number | null {
  const box = view.dom.getBoundingClientRect();
  const hit = view.posAtCoords({ left: box.left + 24, top: event.clientY });
  if (!hit) return null;
  const $pos = view.state.doc.resolve(hit.pos);
  if ($pos.depth === 0) return hit.inside >= 0 ? hit.inside : null;
  return $pos.before(1);
}

export const BlockHandle = Extension.create({
  name: "klarityBlockHandle",
  addProseMirrorPlugins() {
    let current = -1;
    const handle = document.createElement("div");
    handle.className = "kd-handle";
    handle.draggable = true;
    handle.contentEditable = "false";
    handle.setAttribute("aria-hidden", "true");
    handle.title = "Drag to move this block (Alt+Shift+↑/↓)";
    handle.textContent = "⠿";
    const hide = () => {
      handle.dataset.visible = "false";
    };
    return [
      new Plugin({
        key: new PluginKey("klarityBlockHandle"),
        view: (view) => {
          const onDragStart = (e: DragEvent) => {
            if (current >= 0 && e.dataTransfer) startBlockDrag(view, current, e.dataTransfer);
          };
          handle.addEventListener("dragstart", onDragStart);
          handle.addEventListener("dragend", hide);
          view.dom.parentElement?.append(handle);
          return {
            destroy: () => {
              handle.removeEventListener("dragstart", onDragStart);
              handle.removeEventListener("dragend", hide);
              handle.remove();
            },
          };
        },
        props: {
          handleDOMEvents: {
            mousemove: (view, event) => {
              // Lost lock: the handle is a pure control, so it disappears.
              if (!view.editable) {
                hide();
                return false;
              }
              // EditorContent may move view.dom after plugin init; keep the handle next to it.
              const wrapper = view.dom.parentElement;
              if (wrapper && handle.parentElement !== wrapper) wrapper.append(handle);
              const pos = topLevelPosAt(view, event);
              const dom = pos === null ? null : view.nodeDOM(pos);
              if (pos === null || !(dom instanceof HTMLElement)) return false;
              current = pos;
              handle.style.top = `${view.dom.offsetTop + dom.offsetTop}px`;
              handle.dataset.visible = "true";
              return false;
            },
            mouseleave: (_view, event) => {
              if (event.relatedTarget !== handle) hide();
              return false;
            },
            drop: () => {
              hide();
              return false;
            },
          },
        },
      }),
    ];
  },
});
